import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { X, ChevronLeft, ChevronRight, Sparkles, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Pasos del tour. Cada uno apunta a un elemento por CSS selector y describe
// qué hace esa opción. Se usan selectores estables (data-tour) que añadimos
// al DashboardLayout — más seguros que clases o textos que pueden cambiar.
const STEPS = [
  {
    selector: '[data-tour="menu-inicio"]',
    title: 'Inicio',
    body: 'Tu panel principal: aquí ves tus peticiones recientes y el estado de tus trámites.',
    placement: 'right',
  },
  {
    selector: '[data-tour="menu-mis-peticiones"]',
    title: 'Mis Peticiones',
    body: 'Todas las solicitudes que has radicado, con su estado actual (en revisión, aprobado, finalizado, etc.) y documentos asociados.',
    placement: 'right',
  },
  {
    selector: '[data-tour="menu-consulta-predios"]',
    title: 'Consulta de Predios',
    body: 'Busca información catastral de cualquier predio en los municipios donde tienes acceso. Filtros por código predial, dirección o propietario.',
    placement: 'right',
  },
  {
    selector: '[data-tour="menu-visor-predios"]',
    title: 'Visor de Predios',
    body: 'Mapa interactivo: ubica predios por coordenadas, mide áreas y consulta la información catastral espacial.',
    placement: 'right',
  },
  {
    selector: '[data-tour="user-avatar"]',
    title: 'Tu Perfil',
    body: 'Aquí puedes cambiar tu contraseña, consultar la fecha hasta la cual tienes acceso al sistema y cerrar sesión.',
    placement: 'left',
  },
];

const PAD = 8; // padding alrededor del target en el hueco del overlay

export default function EmpresaTour({ open, onClose, onFinish }) {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef(null);

  const isLast = step === STEPS.length - 1;
  const current = STEPS[step];

  // Recalcular posición del hueco y del tooltip
  const measure = useCallback(() => {
    if (!open) return;
    const el = document.querySelector(current.selector);
    if (!el) {
      setRect(null);
      return;
    }
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });

    // Tooltip relativo a viewport. Lo posicionamos según placement.
    const tt = tooltipRef.current;
    const ttW = tt?.offsetWidth || 320;
    const ttH = tt?.offsetHeight || 160;
    const margin = 14;
    let top = r.top + r.height / 2 - ttH / 2;
    let left;
    if (current.placement === 'left') {
      left = r.left - ttW - margin;
    } else {
      left = r.right + margin;
    }
    // Clamp dentro de la viewport
    left = Math.max(12, Math.min(left, window.innerWidth - ttW - 12));
    top = Math.max(12, Math.min(top, window.innerHeight - ttH - 12));
    setTooltipPos({ top, left });
  }, [current, open]);

  useLayoutEffect(() => {
    if (open) {
      // Doble RAF para que React termine de renderizar el target tras el step
      requestAnimationFrame(() => requestAnimationFrame(measure));
    }
  }, [open, step, measure]);

  useEffect(() => {
    if (!open) return;
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [open, measure]);

  // Persistir tutorial_empresa_visto en BD
  const marcarVisto = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/users/me/tutorial-visto`, { visto: true }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) { /* silencio: si falla, el banner reaparecerá la próxima vez */ }
  }, []);

  const handleClose = useCallback(async (saveVisto = true) => {
    if (saveVisto) await marcarVisto();
    setStep(0);
    onClose?.();
  }, [marcarVisto, onClose]);

  const handleFinish = useCallback(async () => {
    await marcarVisto();
    setStep(0);
    onFinish?.();
  }, [marcarVisto, onFinish]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999]" data-testid="empresa-tour">
      {/* Overlay con hueco recortado mediante clip-path. Si no encontramos el
          target (rect=null) hacemos overlay completo. */}
      {rect ? (
        <svg className="absolute inset-0 w-full h-full pointer-events-auto">
          <defs>
            <mask id="tour-mask">
              <rect width="100%" height="100%" fill="white" />
              <rect
                x={rect.left - PAD}
                y={rect.top - PAD}
                width={rect.width + PAD * 2}
                height={rect.height + PAD * 2}
                rx={8} ry={8}
                fill="black"
              />
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(15,23,42,0.65)" mask="url(#tour-mask)" />
          {/* Anillo destacado alrededor del target */}
          <rect
            x={rect.left - PAD}
            y={rect.top - PAD}
            width={rect.width + PAD * 2}
            height={rect.height + PAD * 2}
            rx={8} ry={8}
            fill="none"
            stroke="#00cc5f"
            strokeWidth={2}
            style={{ filter: 'drop-shadow(0 0 8px rgba(16, 185, 129, 0.6))' }}
          />
        </svg>
      ) : (
        <div className="absolute inset-0 bg-slate-900/65 pointer-events-auto" />
      )}

      {/* Tooltip flotante */}
      <div
        ref={tooltipRef}
        className="absolute bg-white rounded-lg shadow-2xl border border-slate-200 w-[320px] p-4 animate-in fade-in slide-in-from-left-2 duration-200"
        style={{ top: tooltipPos.top, left: tooltipPos.left }}
        data-testid="empresa-tour-tooltip"
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 font-bold text-sm">
              {step + 1}
            </span>
            <h3 className="font-semibold text-slate-900">{current.title}</h3>
          </div>
          <button
            onClick={() => handleClose(true)}
            className="text-slate-400 hover:text-slate-600"
            title="Saltar recorrido"
            data-testid="empresa-tour-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-sm text-slate-600 leading-snug mb-3">{current.body}</p>

        {/* Progreso visual + controles */}
        <div className="flex items-center gap-1 mb-3">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? 'bg-emerald-500' : 'bg-slate-200'}`}
            />
          ))}
        </div>

        <div className="flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleClose(true)}
            className="text-slate-500"
            data-testid="empresa-tour-skip"
          >
            Saltar
          </Button>
          <div className="flex items-center gap-1.5">
            <Button aria-label="Anterior"
              variant="outline"
              size="sm"
              onClick={() => setStep(s => Math.max(0, s - 1))}
              disabled={step === 0}
              data-testid="empresa-tour-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {!isLast ? (
              <Button
                size="sm"
                onClick={() => setStep(s => s + 1)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="empresa-tour-next"
              >
                Siguiente <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={handleFinish}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="empresa-tour-finish"
              >
                <CheckCircle className="w-4 h-4 mr-1" /> Finalizar
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Banner que aparece la primera vez para invitar al recorrido.
// Solo se renderiza si user.role === 'empresa' y user.tutorial_empresa_visto !== true.
export function EmpresaTourBanner({ user, onStart, onDismiss }) {
  if (!user || user.role !== 'empresa' || user.tutorial_empresa_visto) return null;
  return (
    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-l-4 border-emerald-500 rounded-lg p-4 mb-4 flex items-center justify-between gap-3 shadow-sm">
      <div className="flex items-center gap-3 min-w-0">
        <Sparkles className="w-5 h-5 text-emerald-600 shrink-0" />
        <div className="min-w-0">
          <p className="font-semibold text-emerald-900">¡Bienvenido a Asomunicipios!</p>
          <p className="text-xs text-emerald-800 truncate">Te llevamos un recorrido rápido por las opciones disponibles para tu rol.</p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button variant="ghost" size="sm" onClick={onDismiss} className="text-emerald-800 hover:bg-emerald-100">
          Más tarde
        </Button>
        <Button size="sm" onClick={onStart} className="bg-emerald-600 hover:bg-emerald-700">
          Empezar recorrido
        </Button>
      </div>
    </div>
  );
}
